import { cn } from '@endure/ui';
import { useState } from 'react';
import { useDisconnect } from 'wagmi';

import primeLogoSrc from 'assets/img/primeLogo.svg';
import { useGetPrimeToken } from 'clients/api';
import { Button, type ButtonProps, Icon, Modal, SecondaryButton, Username } from 'components';
import config from 'config';
import { routes } from 'constants/routing';
import { CopyAddressButton } from 'containers/CopyAddressButton';
import { useSubstrateConnection } from 'libs/substrate';
import { useTranslation } from 'libs/translations';
import { useAccountAddress, useAuthModal, useChainId } from 'libs/wallet';
import { Link } from 'react-router';
import { ChainId } from 'types';
import { evmAddressToSs58Mirror } from 'utilities';

export interface ConnectButtonProps
  extends Omit<
    ButtonProps,
    'isAccountPrime' | 'accountAddress' | 'loading' | 'onClick' | 'variant'
  > {}

export const ConnectButton: React.FC<ConnectButtonProps> = ({ className, ...otherProps }) => {
  const { disconnect } = useDisconnect();
  const { accountAddress } = useAccountAddress();
  const { chainId } = useChainId();

  const { openAuthModal } = useAuthModal();
  const {
    accounts: substrateAccounts,
    connect: connectSubstrate,
    disconnect: disconnectSubstrate,
    errorMessage: substrateErrorMessage,
    selectAccount: selectSubstrateAccount,
    selectedAccount: selectedSubstrateAccount,
    status: substrateStatus,
  } = useSubstrateConnection();

  const { t } = useTranslation();
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const openAccountModal = () => setIsAccountModalOpen(true);
  const closeAccountModal = () => setIsAccountModalOpen(false);
  const closeWalletModal = () => setIsWalletModalOpen(false);

  const handleConnectButtonClick = () => {
    if (accountAddress) {
      openAccountModal();
    } else {
      setIsWalletModalOpen(true);
    }
  };

  const handleConnectEvm = () => {
    closeWalletModal();
    openAuthModal({
      analyticVariant: 'header_connect_button',
    });
  };

  const handleConnectSubstrate = async () => {
    await connectSubstrate();
  };

  const handleDisconnect = () => {
    disconnect();
    closeAccountModal();
  };

  const { data: getPrimeTokenData, isLoading: isGetPrimeTokenLoading } = useGetPrimeToken({
    accountAddress,
  });
  const isAccountPrime = !!getPrimeTokenData?.exists;
  const substrateAddress =
    accountAddress && chainId === ChainId.BITTENSOR_TESTNET
      ? evmAddressToSs58Mirror(accountAddress)
      : undefined;

  if (isGetPrimeTokenLoading) {
    // Reserve the button's footprint so the nav doesn't shift when it resolves
    return (
      <Button className={cn('min-w-[110px]', className)} disabled aria-hidden="true">
        <span className="invisible">{t('connectButton.connect')}</span>
      </Button>
    );
  }

  return (
    <>
      <Button
        onClick={handleConnectButtonClick}
        className={cn(
          accountAddress && 'bg-transparent hover:bg-surface-2 active:bg-surface-2',
          accountAddress && !isAccountPrime && 'border-line',
          accountAddress &&
            isAccountPrime &&
            'border-accent hover:border-accent active:border-accent',
          className,
        )}
        {...otherProps}
      >
        {accountAddress ? (
          <div className="flex items-center gap-x-3">
            {isAccountPrime ? (
              <img className="h-4" src={primeLogoSrc} alt={t('primeButton.primeLogoAlt')} />
            ) : (
              <Icon name="user" className="mb-1 h-4 text-muted" />
            )}

            <Username
              className="max-w-30 sm:max-w-full"
              showProvider={false}
              showTooltip={false}
              address={accountAddress}
              shouldEllipseAddress
            />

            <Icon name="expand" className="hidden text-muted sm:block" />
          </div>
        ) : (
          t('connectButton.connect')
        )}
      </Button>

      <Modal isOpen={isAccountModalOpen} handleClose={closeAccountModal}>
        <div className="space-y-10">
          {!!accountAddress && (
            <div className="space-y-4">
              <div className="flex truncate items-center justify-between space-x-2 break-all">
                <Username address={accountAddress} shouldEllipseAddress={false} showCopyAddress />
              </div>

              {!!substrateAddress && (
                <div className="rounded-lg border border-line bg-surface px-4 py-3">
                  <p className="mb-2 text-xs font-semibold uppercase text-muted">
                    Substrate wallet
                  </p>
                  <div className="flex items-center justify-between gap-x-3">
                    <span className="break-all font-mono text-sm">{substrateAddress}</span>
                    <CopyAddressButton
                      className="shrink-0"
                      address={substrateAddress}
                      showTooltip
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="rounded-lg border border-line bg-surface px-4 py-3">
            <div className="mb-3 flex items-center justify-between gap-x-4">
              <div>
                <p className="text-xs font-semibold uppercase text-muted">Bittensor wallet</p>
                <p className="mt-1 text-sm text-foreground">
                  {selectedSubstrateAccount
                    ? selectedSubstrateAccount.name || selectedSubstrateAccount.address
                    : 'Not connected'}
                </p>
              </div>

              {selectedSubstrateAccount ? (
                <SecondaryButton onClick={disconnectSubstrate} className="shrink-0">
                  Disconnect
                </SecondaryButton>
              ) : (
                <SecondaryButton
                  onClick={handleConnectSubstrate}
                  className="shrink-0"
                  disabled={substrateStatus === 'connecting'}
                >
                  {substrateStatus === 'connecting' ? 'Connecting...' : 'Connect'}
                </SecondaryButton>
              )}
            </div>

            {selectedSubstrateAccount &&
              (substrateAccounts.length > 1 ? (
                <select
                  aria-label="Select Bittensor account"
                  className="w-full rounded-lg border border-line bg-elevated px-3 py-2 font-mono text-sm text-foreground"
                  onChange={(event) => selectSubstrateAccount(event.currentTarget.value)}
                  value={selectedSubstrateAccount.address}
                >
                  {substrateAccounts.map((account) => (
                    <option key={account.address} value={account.address}>
                      {account.name ? `${account.name} · ${account.address}` : account.address}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="break-all font-mono text-sm text-muted">
                  {selectedSubstrateAccount.address}
                </p>
              ))}

            {substrateErrorMessage && (
              <p className="mt-3 text-sm text-red">{substrateErrorMessage}</p>
            )}
          </div>

          <Link to={routes.getStarted.path} onClick={closeAccountModal} className="block">
            <SecondaryButton className="w-full">Open onboarding guide</SecondaryButton>
          </Link>

          {/* When running in Safe Wallet app, user's wallet should be kept connected at all time */}
          {!config.isSafeApp && (
            <SecondaryButton onClick={handleDisconnect} className="w-full">
              {t('connectButton.disconnect')}
            </SecondaryButton>
          )}
        </div>
      </Modal>

      <Modal isOpen={isWalletModalOpen} handleClose={closeWalletModal}>
        <div className="space-y-6">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase text-accent-strong">Connect wallets</p>
            <h2 className="text-2xl font-semibold text-foreground">Choose a wallet to connect</h2>
            <p className="text-sm text-muted">
              Forge uses your EVM wallet for lending transactions and your Bittensor wallet for
              native TAO and Alpha moves.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Button onClick={handleConnectEvm} className="w-full">
              Connect EVM wallet
            </Button>

            <SecondaryButton
              onClick={handleConnectSubstrate}
              className="w-full"
              disabled={substrateStatus === 'connecting'}
            >
              {substrateStatus === 'connecting' ? 'Connecting...' : 'Connect Bittensor wallet'}
            </SecondaryButton>
          </div>

          {substrateAccounts.length > 0 && (
            <p className="text-sm text-success">
              Bittensor wallet connected. Continue to onboarding.
            </p>
          )}

          {substrateErrorMessage && <p className="text-sm text-red">{substrateErrorMessage}</p>}

          <Link to={routes.getStarted.path} onClick={closeWalletModal} className="block">
            <SecondaryButton className="w-full">Open onboarding guide</SecondaryButton>
          </Link>
        </div>
      </Modal>
    </>
  );
};
